import React, { useState, useMemo, useEffect, useRef, SyntheticEvent } from 'react';
import classNames from 'classnames/bind';
import { useInView } from 'react-intersection-observer';
import styles from './index.pcss';
import { VideoShimmer } from './VideoShimmer';
import ProgressBar from '../../atoms/ProgressBar';
import properties from '../../../modules/ajio-util/properties';
import { getImageNameForGTMPromotion } from '../../../modules/ga/gtmLandingPageMethods';
import { formatDuration } from '../../../utils/banner';
import { gtmEventPush, gtmPagePush, segmentEventPush } from '../../../modules/ga/GTMdatalayer';
import Text from '../../atoms/Text';
import { videoBannerInfo } from './gaEventTypes';
import { getImagePath } from '../../../fleek/utils';
import { replaceWithJioCDN } from '../../../../src/utils/imageHandling';

const PLAYING_DEBOUNCE_TIME = 50;
const WAITING_DEBOUNCE_TIME = 200;

interface Video {
  autoPlay: boolean;
  muted: boolean;
  handleMuteClick?: () => void;
  onMuteClick?: () => void;
  loop: boolean;
  displayProgressBar: boolean;
  showPlayPauseHandler: boolean;
  onClickOpenModal: boolean;
  onClickHandler: () => void;
  isModal: boolean;
  deviceType: string;
  displayTopGradient: boolean;
  displayBottomGradient: boolean;
  showTimestamp: boolean;
  videoURL: string;
  isShopThelook: boolean;
  rowNumber: any;
  currentIndex: number;
  borderRadius: number;
  scrollBarExits: boolean;
  isLuxe: boolean;
  updateVideoDetails: ({
    name,
    loadTime,
    currentIndex,
    videTotalViewTime
  }: {
    name: string;
    loadTime: number;
    currentIndex: number;
    videTotalViewTime: number;
  }) => void;
  thumbnailImage: string;
  videoIsOnBanner: boolean;
  isPDP?: boolean;
  isFloatingWidget?: boolean;
  triggerOnVideoEnd?: () => void;
  triggerVideoImpression?: boolean;
  triggerVideoViewedGA?: boolean;
  isFleek: boolean;
  objectFit?: any | undefined;
  muteIconRight: boolean;
  muteIconRightMargin?: string;
  tiggerOnView?: () => void;
  deferTime?: any;
  deferAutoplay?: boolean;
  colNumber?: any;
  showPlayIcon?: boolean;
  pdpVideos?: boolean;
  minimizeVideoClick: () => void;
  productName?: string;
  productCode?: string;
  maximizedOpen?: boolean;
  productDetails?: any;
}

const cx = classNames.bind(styles);

function Video({
  autoPlay,
  muted,
  onMuteClick,
  loop = false,
  videoURL = '',
  displayProgressBar = false,
  showPlayPauseHandler = true,
  onClickOpenModal = false,
  isModal = false,
  displayTopGradient = true,
  displayBottomGradient = false,
  deviceType = 'msite',
  showTimestamp = false,
  isShopThelook = false,
  currentIndex = 0,
  rowNumber,
  scrollBarExits = false,
  isLuxe = false,
  updateVideoDetails,
  thumbnailImage = '',
  videoIsOnBanner = true,
  isPDP = false,
  isFloatingWidget = false,
  triggerOnVideoEnd,
  triggerVideoImpression = true,
  triggerVideoViewedGA = true,
  isFleek = false,
  muteIconRight = false,
  objectFit = undefined,
  isFleekPdp = false,
  isKnowTheProduct = false,
  muteIconRightMargin,
  tiggerOnView,
  deferAutoplay = false,
  deferTime = 1,
  colNumber = '',
  onClickHandler,
  borderRadius = 0,
  showPlayIcon = false,
  pdpVideos = false,
  minimizeVideoClick,
  productCode,
  productName,
  maximizedOpen,
  productDetails
}: Video) {
  const [mute, setMute] = useState(muted ? true : false);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const videoRef = useRef(null);
  const [inViewRef, videoIsOnView] = useInView({
    threshold: 0.8,
    rootMargin: '10px'
  });
  const [isPlaying, setIsPlaying] = useState(!!(autoPlay && videoIsOnView));
  const [showPauseButton, setShowPauseButton] = useState(false);

  const isWaitingTimeout = useRef(null);
  const isPlayingTimeout = useRef(null);
  const timerRef = useRef();
  const isLongPress = useRef(false);

  const [loadTime, setLoadTime] = useState(0);
  const [canPlayTime, setCanPlayTime] = useState(0);
  const [noOfTimesPlayed, setNoOfTimesPlayed] = useState(0);
  const videoName = getImageNameForGTMPromotion(videoURL);
  const [previousViewTime, setPreviousViewTime] = useState(0);
  const [recordLoadTime, setRecordLoadTime] = useState(true);
  const [triggerVideoViewed, setTriggerVideoViewed] = useState(true);
  const [triggerGAFunctions, setTriggerGAFunctions] = useState(videoIsOnBanner);
  const [videoWasOnViewPort, setVideoWasOnViewPort] = useState(false);
  const [impressionTriggered, setImpressionTriggered] = useState(false);
  // let eventName = isModal ?  'video_screen_interaction' : 'landing_screen_interactions' ;
  // let categoryName = isShopThelook ? 'shop the look video interactions' : 'video_banner_interactions';
  // let pageName = isModal ? isShopThelook ? 'shop the look video screen' : 'video banner screen' : 'landing' ;
  const positionRef = useRef(position);
  const noOfTimesPlayedRef = useRef(noOfTimesPlayed);
  const durationRef = useRef(duration);
  const previousViewTimeRef = useRef(previousViewTime);
  const videoLoadTimeRef = useRef(canPlayTime);
  const [isLoadingError, setIsLoadingError] = useState(false);
  const [isVideoPlayedOnce, setVideoPlayedOnce] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(maximizedOpen || false);

  useEffect(() => {
    if(pdpVideos && !isVideoPlayedOnce && videoWasOnViewPort) {
      gtmEventPush({
        page_name: 'pdp screen',
        page_type: 'pdp screen',
        event: 'pdp_catalog_video_interactions',
        event_category: 'pdp_catalog_video_interactions',
        event_action: 'video played',
        event_label: maximizedOpen ? 'full screen' : 'minimized',
        product_id: productCode,
        product_name: productName
      });
      segmentEventPush({
        page_name: 'pdp screen',
        page_type: 'pdp screen',
        event: 'Pdp Product Image Details Interaction',
        product: [{
          product_brand: productDetails?.brandName,
          product_brick: productDetails?.brickName,
          product_id: productDetails?.code || productDetails?.baseProduct,
          product_list_price: productDetails?.price?.discountValue,
          product_mrp: productDetails?.price?.value,
          product_name: productDetails?.name,
          product_rating: productDetails?.aggregateRating?.averageRating,
        }],
        video_length: position,
        video_seen: videoEnded ? true : false,
        video_pause_resume: isPlaying ? 'pause' : 'resume',
        video_mute: mute ?  'false' : 'true',
        video_impression: true,
        video_screen: isModal ? 'fullscreen' : 'minimized',
      });
      setVideoPlayedOnce(true);
    }
  }, [isPlaying]);

  useEffect(() => {
    positionRef.current = position;
    noOfTimesPlayedRef.current = noOfTimesPlayed;
    durationRef.current = duration;
    previousViewTimeRef.current = previousViewTime;
    videoLoadTimeRef.current = canPlayTime;
  }, [position, noOfTimesPlayed, duration, previousViewTime, canPlayTime]);
 
  let bannerInfoType = !isFloatingWidget
    ? isModal
      ? isShopThelook
        ? 'shopTheLook'
        : ''
      : isPDP
      ? 'pdp'
      : 'landing'
    : isModal
    ? 'floatingWidgetModal'
    : 'floatingWidgetCard';
  let { eventName, categoryName, pageName } = videoBannerInfo(bannerInfoType);
  const pageType = pageName;
  if (pageName === 'landing') pageName = 'home landing screen';

  useEffect(() => {
    if (isModal && (!isShopThelook || isPDP)) {
      gtmPagePush('video banner screen', 'video banner screen', '', {
        store: isLuxe ? 'luxe' : 'ajio'
      });
    }
  }, [isModal, isShopThelook, isPDP]);

  useEffect(() => {
    setLoading(true);
  }, []);

  useEffect(() => {
    setTriggerGAFunctions(videoIsOnBanner);
  }, [videoIsOnBanner]);

  useEffect(() => {
    if (isPlaying) {
      setVideoEnded(false);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    const waitingHandler = () => {
      clearTimeout(isWaitingTimeout.current);
      isWaitingTimeout.current = setTimeout(() => {
        setIsWaiting(true);
      }, WAITING_DEBOUNCE_TIME);
    };

    const playHandler = () => {
      clearTimeout(isWaitingTimeout.current);
      clearTimeout(isPlayingTimeout.current);
      isPlayingTimeout.current = setTimeout(() => {
        setIsPlaying(true);
        setIsWaiting(false);
      }, PLAYING_DEBOUNCE_TIME);
    };

    const pauseHandler = () => {
      clearTimeout(isWaitingTimeout.current);
      clearTimeout(isPlayingTimeout.current);
      isPlayingTimeout.current = setTimeout(() => {
        setIsPlaying(false);
        setIsWaiting(false);
      }, PLAYING_DEBOUNCE_TIME);
    };

    const element = videoRef.current;

    element.addEventListener('waiting', waitingHandler);
    element.addEventListener('play', playHandler);
    element.addEventListener('playing', playHandler);
    element.addEventListener('pause', pauseHandler);

    // clean up
    return () => {
      clearTimeout(isWaitingTimeout.current);
      clearTimeout(isPlayingTimeout.current);

      element.removeEventListener('waiting', waitingHandler);
      element.removeEventListener('play', playHandler);
      element.removeEventListener('playing', playHandler);
      element.removeEventListener('pause', pauseHandler);
    };
  }, [videoRef]);

  useEffect(() => {
    if (autoPlay && videoIsOnView) {
      if (sessionStorage.getItem(`${videoURL} ${rowNumber} ${colNumber}`)) {
        const timeToStart = sessionStorage.getItem(`${videoURL} ${rowNumber} ${colNumber}`);
        if (Number(timeToStart) < duration) {
          videoRef.current.currentTime = timeToStart;
          setPosition(Number(timeToStart));
        }
      }

      if (deferAutoplay) {
        const timer = setTimeout(() => {
          console.log('DEFER AUTOPLAY....');
          videoRef.current.play();
        }, deferTime * 1000);

        return () => {
          clearTimeout(timer);
        };
      } else {
        videoRef.current.play();
      }
    } else {
      videoRef.current.pause();
    }
  }, [autoPlay, videoIsOnView]);

  /* used only for pdp */
  useEffect(() => {
    if (isPDP && videoIsOnView && !isModal) {
      if (sessionStorage.getItem(`${videoURL} ${rowNumber} ${colNumber}`)) {
        let currentPlayingVideo = `${videoURL} ${rowNumber} ${colNumber}`;
        let lastPlayedVideo = sessionStorage.getItem('lastPlayedVideoInPDP');
        let isPlayLastVideo = sessionStorage.getItem('isPlayLastVideo');
        if (isPlayLastVideo == 'false') {
          return;
        }
        if (currentPlayingVideo != lastPlayedVideo) {
          return;
        }
        setIsPlaying(initial => !initial);
        setIsWaiting(false);
        const timeToStart = sessionStorage.getItem(lastPlayedVideo);
        if (Number(timeToStart) < duration) {
          videoRef.current.currentTime = timeToStart;
          setPosition(Number(timeToStart));
        }
        videoRef.current.play();
      }
    }
  }, [isPDP, videoIsOnView]);

  useEffect(() => {
    const canPlayTime = videoLoadTimeRef.current;
    if (videoIsOnView) {
      if (sessionStorage.getItem(`${videoURL} ${rowNumber} ${colNumber}`)) {
        const viewedTime = sessionStorage.getItem(`${videoURL} ${rowNumber} ${colNumber}`);
        setPreviousViewTime(Math.round(Number(viewedTime)));
      }
      setVideoWasOnViewPort(true);
      if (triggerGAFunctions && !impressionTriggered && triggerVideoImpression && !pdpVideos) {
        gtmEventPush({
          event: eventName,
          event_category: categoryName,
          event_action: 'video impression',
          video_name: videoName,
          is_auto_play_enabled: autoPlay,
          video_load_time: canPlayTime,
          store_type: isLuxe ? 'luxe' : 'ajio',
          page_name: pageName,
          page_type: pageType
        });
        tiggerOnView && tiggerOnView();
        setImpressionTriggered(true);
      }
    } else {
      const position = positionRef.current;
      const noOfTimesPlayed = noOfTimesPlayedRef.current;
      const duration = durationRef.current;
      const previousViewTime = previousViewTimeRef.current;
      const viewTime =
        noOfTimesPlayed === 0
          ? Math.abs(Math.round(Number(position)) - Number(previousViewTime))
          : Math.abs(
              Math.round(Number(position)) +
                Math.round(Number(duration)) * noOfTimesPlayed -
                Number(previousViewTime)
            );
      if (triggerGAFunctions && videoWasOnViewPort && !pdpVideos) {
        gtmEventPush({
          event: eventName,
          event_category: categoryName,
          event_action: 'video view time',
          video_name: videoName,
          is_auto_play_enabled: autoPlay,
          video_load_time: canPlayTime,
          video_total_view_time: viewTime,
          store_type: isLuxe ? 'luxe' : 'ajio',
          page_name: pageName,
          page_type: pageType
        });
        setVideoWasOnViewPort(false);
      }
      setNoOfTimesPlayed(0);
      setTriggerVideoViewed(true);
    }
  }, [videoIsOnView, triggerGAFunctions, videoWasOnViewPort, impressionTriggered]);

  useEffect(() => {
    return () => {
      const position = positionRef.current;
      const noOfTimesPlayed = noOfTimesPlayedRef.current;
      const duration = durationRef.current;
      const previousViewTime = previousViewTimeRef.current;
      const canPlayTime = videoLoadTimeRef.current;
      const viewTime =
        noOfTimesPlayed === 0
          ? Math.abs(Math.round(Number(position)) - Number(previousViewTime))
          : Math.abs(
              Math.round(Number(position)) +
                Math.round(Number(duration)) * noOfTimesPlayed -
                Number(previousViewTime)
            );
      if (triggerGAFunctions) {
        gtmEventPush({
          event: eventName,
          event_category: categoryName,
          event_action: 'video view time',
          video_name: videoName,
          is_auto_play_enabled: autoPlay,
          video_load_time: canPlayTime,
          video_total_view_time: viewTime,
          store_type: isLuxe ? 'luxe' : 'ajio',
          page_name: pageName,
          page_type: pageType
        });
      }
    };
  }, [triggerGAFunctions]);

  useEffect(() => {
    if (!!updateVideoDetails && videoIsOnView) {
      let viewTime = Math.abs(
        Number(position) + Number(duration) * noOfTimesPlayed - Number(previousViewTime)
      );
      updateVideoDetails({
        name: videoName,
        loadTime: canPlayTime,
        currentIndex,
        videTotalViewTime: viewTime
      });
    }
  }, [canPlayTime, videoName, videoIsOnView, currentIndex]);

  useEffect(() => {
    videoRef.current.muted = muted;
    setMute(muted);
  }, [muted]);

  useEffect(() => {
    const canPlayTime = videoLoadTimeRef.current;
    if (loop && videoEnded && videoIsOnView) {
      setNoOfTimesPlayed(initial => initial + 1);
      videoRef.current.play();
      setTriggerVideoViewed(true);
      setIsPlaying(initial => !initial);
      if (triggerGAFunctions) {
        gtmEventPush({
          event: eventName,
          event_category: categoryName,
          event_action: isModal ? 'full screen' : 'minimized',
          event_label: 'video looped',
          video_name: videoName,
          is_auto_play_enabled: autoPlay,
          video_load_time: canPlayTime,
          video_loops: 1,
          store_type: isLuxe ? 'luxe' : 'ajio',
          page_name: pageName,
          page_type: pageType
        });
        if(pdpVideos){
          segmentEventPush({
            event: 'Pdp Product Image Details Interaction',
            event_category: categoryName,
            event_action: isModal ? 'full screen' : 'minimized',
            event_label: 'video looped',
            video_name: videoName,
            product: [{
              product_brand: productDetails?.brandName,
              product_brick: productDetails?.brickName,
              product_id: productDetails?.code || productDetails?.baseProduct,
              product_list_price: productDetails?.price?.discountValue,
              product_mrp: productDetails?.price?.value,
              product_name: productDetails?.name,
              product_rating: productDetails?.aggregateRating?.averageRating,
            }],
            video_length: position,
            video_seen: videoEnded ? true : false,
            video_pause_resume: isPlaying ? 'pause' : 'resume',
            video_mute: mute ?  'false' : 'true',
            video_impression: true,
            video_screen: isModal ? 'fullscreen' : 'minimized',
        });
        }
      }
      setVideoEnded(false);
    }
  }, [loop, videoEnded, videoIsOnView, triggerGAFunctions]);

  const playPauseHandler = () => {
    if (!isPlaying) {
      videoRef.current.play();
      setIsPlaying(initial => !initial);
      if (triggerGAFunctions) {
        gtmEventPush({
          event: eventName,
          event_category: categoryName,
          event_action: 'video play',
          event_label: isModal ? 'full screen' : 'minimized',
          video_name: videoName,
          is_auto_play_enabled: autoPlay,
          video_load_time: canPlayTime,
          store_type: isLuxe ? 'luxe' : 'ajio',
          page_name: pageName,
          page_type: pageType
        });
        if(pdpVideos){
          segmentEventPush({
            event: 'Pdp Product Image Details Interaction',
            event_category: categoryName,
            event_action: 'video play',
            product: [{
              product_brand: productDetails?.brandName,
              product_brick: productDetails?.brickName,
              product_id: productDetails?.code || productDetails?.baseProduct,
              product_list_price: productDetails?.price?.discountValue,
              product_mrp: productDetails?.price?.value,
              product_name: productDetails?.name,
              product_rating: productDetails?.aggregateRating?.averageRating,
            }],
            video_name: videoName,
            video_length: position,
            video_seen: videoEnded ? true : false,
            video_pause_resume: isPlaying ? 'pause' : 'resume',
            video_mute: mute ?  'false' : 'true',
            video_impression: true,
            video_screen: isModal ? 'fullscreen' : 'minimized',
        });
        }
      }
    } else {
      if (isPDP && !isModal) {
        sessionStorage.setItem('isPlayLastVideo', 'false');
      }
      videoRef.current.pause();
      setIsPlaying(initial => !initial);
      if (triggerGAFunctions) {
        gtmEventPush({
          event: eventName,
          event_category: categoryName,
          event_action: isModal ? 'full screen' : 'minimized',
          event_label: 'single tap',
          video_name: videoName,
          is_auto_play_enabled: autoPlay,
          video_load_time: canPlayTime,
          store_type: isLuxe ? 'luxe' : 'ajio',
          page_name: pageName,
          page_type: pageType
        });
        if(pdpVideos){
          segmentEventPush({
            event: 'Pdp Product Image Details Interaction',
            event_category: categoryName,
            event_label: 'single tap',
            video_name: videoName,
            product: [{
              product_brand: productDetails?.brandName,
              product_brick: productDetails?.brickName,
              product_id: productDetails?.code || productDetails?.baseProduct,
              product_list_price: productDetails?.price?.discountValue,
              product_mrp: productDetails?.price?.value,
              product_name: productDetails?.name,
              product_rating: productDetails?.aggregateRating?.averageRating,
            }],
            video_length: position,
            video_seen: videoEnded ? true : false,
            video_pause_resume: isPlaying ? 'pause' : 'resume',
            video_mute: mute ?  'false' : 'true',
            video_impression: true,
            video_screen: isModal ? 'fullscreen' : 'minimized',
        });
        }
      }
    }
  };

  const muteHandler = e => {
    console.log('mute handler getting called');
    if (onMuteClick) {
      onMuteClick();
    }

    videoRef.current.muted = !mute;
    setMute(initial => !initial);

    gtmEventPush({
      event: eventName,
      event_category: categoryName,
      event_action: isModal ? 'full screen' : 'minimized',
      event_label: mute ? 'unmute' : 'mute',
      video_name: videoName,
      is_auto_play_enabled: autoPlay,
      video_load_time: canPlayTime,
      store_type: isLuxe ? 'luxe' : 'ajio',
      page_name: pageName,
      page_type: pageType
    });
    if(pdpVideos){
      segmentEventPush({
        event: 'Pdp Product Image Details Interaction',
        event_category: categoryName,
        product: [{
          product_brand: productDetails?.brandName,
          product_brick: productDetails?.brickName,
          product_id: productDetails?.code || productDetails?.baseProduct,
          product_list_price: productDetails?.price?.discountValue,
          product_mrp: productDetails?.price?.value,
          product_name: productDetails?.name,
          product_rating: productDetails?.aggregateRating?.averageRating,
        }],
        video_name: videoName,
        video_length: position,
        video_seen: videoEnded ? true : false,
        video_pause_resume: isPlaying ? 'pause' : 'resume',
        video_mute: mute ?  'false' : 'true',
        video_impression: true,
        video_screen: isModal ? 'fullscreen' : 'minimized',
    });
    }
    e.preventDefault();
  };

  const durationChangeHandler = ({ target: { duration } }: SyntheticEvent<HTMLVideoElement>) => {
    setDuration(duration);
  };
  const positionChangeHandler = ({
    target: { currentTime: position }
  }: SyntheticEvent<HTMLVideoElement>) => {
    setPosition(position);
    setRemainingTime(duration - position);
    if (isPDP) {
      let lastPlayedVideo = sessionStorage.getItem('lastPlayedVideoInPDP') || '';
      if (lastPlayedVideo !== `${videoURL} ${rowNumber} ${colNumber}`) {
        videoRef.current.pause();
        setTimeout(() => {
          sessionStorage.removeItem(`${videoURL} ${rowNumber} ${colNumber}`);
        }, 3000);
      }
    }
    sessionStorage.setItem(`${videoURL} ${rowNumber} ${colNumber}`, position);
    const viewTime =
      noOfTimesPlayed === 0
        ? Math.abs(Number(position) - Number(previousViewTime))
        : Math.abs(
            Number(position) + Number(duration) * noOfTimesPlayed - Number(previousViewTime)
          );
    if (viewTime <= 3 && viewTime >= 2 && triggerVideoViewed && triggerVideoViewedGA) {
      if (triggerGAFunctions || isPDP) {
        gtmEventPush({
          event: eventName,
          event_category: categoryName,
          event_action: 'video view',
          event_label: isModal ? 'full screen' : 'minimized',
          video_name: videoName,
          is_auto_play_enabled: autoPlay,
          video_load_time: canPlayTime,
          video_views: 1,
          store_type: isLuxe ? 'luxe' : 'ajio',
          page_name: pageName,
          page_type: pageType
        });
        if(pdpVideos){
          segmentEventPush({
            event: 'Pdp Product Image Details Interaction',
            event_category: categoryName,
            event_action: 'video view',
            video_name: videoName,
            product: [{
              product_brand: productDetails?.brandName,
              product_brick: productDetails?.brickName,
              product_id: productDetails?.code || productDetails?.baseProduct,
              product_list_price: productDetails?.price?.discountValue,
              product_mrp: productDetails?.price?.value,
              product_name: productDetails?.name,
              product_rating: productDetails?.aggregateRating?.averageRating,
            }],
            video_length: position,
            video_seen: videoEnded ? true : false,
            video_pause_resume: isPlaying ? 'pause' : 'resume',
            video_mute: mute ?  'false' : 'true',
            video_impression: true,
            video_screen: isModal ? 'fullscreen' : 'minimized',
        });
        }
      }
      setTriggerVideoViewed(false);
    }
  };
  const onReady = () => {
    setLoading(false);
    if (!isReady && sessionStorage.getItem(`${videoURL} ${rowNumber} ${colNumber}`)) {
      const timeToStart = sessionStorage.getItem(`${videoURL} ${rowNumber} ${colNumber}`);
      if (Number(timeToStart) < duration) {
        videoRef.current.currentTime = timeToStart;
        setPosition(Number(timeToStart));
      } else {
        videoRef.current.play();
      }
    }
    setIsReady(true);
  };

  const loadStartHandler = () => {
    if (recordLoadTime) {
      const loadStartingTime = Date.now();
      setLoadTime(loadStartingTime);
    }
  };
  const canPlayThroughHandler = () => {
    if (recordLoadTime) {
      const timeAtVideoCanBePlayed = Date.now();
      setCanPlayTime(Math.round((timeAtVideoCanBePlayed - loadTime) / 1000));
      setRecordLoadTime(false);
    }
  };

  const videoEndedHandler = () => {
    videoRef.current.currentTime = 1;
    setVideoEnded(true);
    if (triggerOnVideoEnd) {
      triggerOnVideoEnd();
    }
  };
  const replayHandler = () => {
    videoRef.current.play();
    setIsPlaying(initial => !initial);
    setTriggerVideoViewed(true);

    gtmEventPush({
      event: eventName,
      event_category: categoryName,
      event_action: isModal ? 'full screen' : 'minimized',
      event_label: 'reload click',
      video_name: videoName,
      is_auto_play_enabled: autoPlay,
      video_load_time: canPlayTime,
      video_loops: 1,
      store_type: isLuxe ? 'luxe' : 'ajio',
      page_name: pageName,
      page_type: pageType
    });
    if(pdpVideos){
      segmentEventPush({
        event: 'Pdp Product Image Details Interaction',
        event_category: categoryName,
        event_action: isModal ? 'full screen' : 'minimized',
        event_label: 'reload click',
        video_name: videoName,
        product: [{
          product_brand: productDetails?.brandName,
          product_brick: productDetails?.brickName,
          product_id: productDetails?.code || productDetails?.baseProduct,
          product_list_price: productDetails?.price?.discountValue,
          product_mrp: productDetails?.price?.value,
          product_name: productDetails?.name,
          product_rating: productDetails?.aggregateRating?.averageRating,
        }],
        video_length: position,
        video_seen: videoEnded ? true : false,
        video_pause_resume: isPlaying ? 'pause' : 'resume',
        video_mute: mute ?  'false' : 'true',
        video_impression: true,
        video_screen: isModal ? 'fullscreen' : 'minimized',
    });
    }
  };

  const expandVideoOnClick = () => {
    if (isLongPress.current) {
      return;
    }
    if (onClickHandler) {
      videoRef.current.pause();
      setIsPlaying(false);
      setIsFullScreen(false);
      onClickHandler();
      gtmEventPush({
        event: eventName,
        event_category: categoryName,
        event_action: 'minimized',
        event_label: 'expand full screen',
        video_name: videoName,
        is_auto_play_enabled: autoPlay,
        video_load_time: canPlayTime,
        store_type: isLuxe ? 'luxe' : 'ajio',
        page_name: pageName,
        page_type: pageType
      });
    }
  };

  const handleMinimizeClick = () => {
    videoRef.current.pause();
    setIsPlaying(false);
    setIsFullScreen(false);
    minimizeVideoClick();
  }

  const pauseVideoOnClick = () => {
    console.log('gets called check');
    if (pdpVideos) {
    console.log('gets called check 2');
      if(isPlaying) {
      console.log('gets called check 3');
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
      segmentEventPush({
        event: 'Pdp Product Image Details Interaction',
        event_category: categoryName,
        event_action: 'minimized',
        event_label: 'expand full screen',
        video_name: videoName,
        product: [{
          product_brand: productDetails?.brandName,
          product_brick: productDetails?.brickName,
          product_id: productDetails?.code || productDetails?.baseProduct,
          product_list_price: productDetails?.price?.discountValue,
          product_mrp: productDetails?.price?.value,
          product_name: productDetails?.name,
          product_rating: productDetails?.aggregateRating?.averageRating,
        }],
        video_length: position,
        video_seen: videoEnded ? true : false,
        video_pause_resume: isPlaying ? 'pause' : 'resume',
        video_mute: mute ?  'false' : 'true',
        video_impression: true,
        video_screen: isModal ? 'fullscreen' : 'minimized',
        });
    } else {
      console.log('gets called check 4');
      if (isLongPress.current) {
        return;
      }
      if (onClickOpenModal) {
        setIsPlaying(!isPlaying);
        isPlaying && videoRef.current.pause();
        !isPlaying && videoRef.current.play();
        onClickHandler();
        gtmEventPush({
          event: eventName,
          event_category: categoryName,
          event_action: 'minimized',
          event_label: 'expand full screen',
          video_name: videoName,
          is_auto_play_enabled: autoPlay,
          video_load_time: canPlayTime,
          store_type: isLuxe ? 'luxe' : 'ajio',
          page_name: pageName,
          page_type: pageType
        });
      }
      // added condition for handling click event for the fleek
      if (isFleek) {
        onClickHandler();
      }
      if (((isModal && isShopThelook) || isPDP) && deviceType === 'msite') {
        playPauseHandler();
      }
    }
  };

  const bannerPlayHandler = () => {
    if (pdpVideos) {
      videoRef.current.play();
      setIsPlaying(initial => !initial);
      setShowPauseButton(initial => !initial);
      setTimeout(() => {
        setShowPauseButton(initial => !initial);
      }, 2000);
    }
    else if (onClickOpenModal) {
    console.log('check inside');
      onClickHandler();
      gtmEventPush({
        event: 'landing_screen_interactions',
        event_category: 'video_banner_interactions',
        event_action: 'minimized',
        event_label: 'expand full screen',
        video_name: videoName,
        is_auto_play_enabled: autoPlay,
        video_load_time: canPlayTime,
        store_type: isLuxe ? 'luxe' : 'ajio',
        page_name: 'home landing screen',
        page_type: 'landing'
      });
    }
    else {
      if (isPDP) {
        sessionStorage.setItem('lastPlayedVideoInPDP', `${videoURL} ${rowNumber} ${colNumber}`);
        sessionStorage.setItem('isPlayLastVideo', 'true');
        setShowPauseButton(initial => !initial);
        setTimeout(() => {
          setShowPauseButton(initial => !initial);
        }, 2000);
      }
      videoRef.current.play();
      setIsPlaying(initial => !initial);
      if (isShopThelook) {
        gtmEventPush({
          event: 'video_screen_interaction',
          event_category: 'shop the look video interactions',
          event_action: 'video play',
          event_label: 'full screen',
          video_name: videoName,
          is_auto_play_enabled: autoPlay,
          video_load_time: canPlayTime,
          store_type: isLuxe ? 'luxe' : 'ajio',
          page_name: 'shop the look video screen',
          page_type: 'shop the look video screen'
        });
      } else {
        gtmEventPush({
          event: eventName,
          event_category: categoryName,
          event_action: 'video played',
          event_label: isModal ? 'full screen' : 'minimized',
          video_name: videoName,
          is_auto_play_enabled: autoPlay,
          video_load_time: canPlayTime,
          store_type: isLuxe ? 'luxe' : 'ajio',
          page_name: pageName,
          page_type: pageType
        });
      }
    }
  };

  const startPressTimer = () => {
    isLongPress.current = false;
    timerRef.current = setTimeout(() => {
      isLongPress.current = true;
    }, 500);
  };

  const onTouchStartHandler = () => {
    startPressTimer();
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(initial => !initial);
      gtmEventPush({
        event: eventName,
        event_category: categoryName,
        event_action: 'full screen',
        event_label: 'press & hold',
        video_name: videoName,
        is_auto_play_enabled: autoPlay,
        video_load_time: canPlayTime,
        store_type: isLuxe ? 'luxe' : 'ajio',
        page_name: pageName,
        page_type: pageType
      });
      if(pdpVideos){
        segmentEventPush({
          event: 'Pdp Product Image Details Interaction',
          event_category: categoryName,
          event_action: 'full screen',
          event_label: 'press & hold',
          video_name: videoName,
          product: [{
            product_brand: productDetails?.brandName,
            product_brick: productDetails?.brickName,
            product_id: productDetails?.code || productDetails?.baseProduct,
            product_list_price: productDetails?.price?.discountValue,
            product_mrp: productDetails?.price?.value,
            product_name: productDetails?.name,
            product_rating: productDetails?.aggregateRating?.averageRating,
          }],
          video_length: position,
          video_seen: videoEnded ? true : false,
          video_pause_resume: isPlaying ? 'pause' : 'resume',
          video_mute: mute ?  'false' : 'true',
          video_impression: true,
          video_screen: isModal ? 'fullscreen' : 'minimized',
      });
      }
    }
  };
  const onTouchEndHandler = () => {
    clearTimeout(timerRef.current);
    if (!isPlaying) {
      videoRef.current.play();
      setIsPlaying(initial => !initial);
      gtmEventPush({
        event: eventName,
        event_category: categoryName,
        event_action: 'full screen',
        event_label: 'press & hold',
        video_name: videoName,
        is_auto_play_enabled: autoPlay,
        video_load_time: canPlayTime,
        store_type: isLuxe ? 'luxe' : 'ajio',
        page_name: pageName,
        page_type: pageType
      });
      if(pdpVideos){
        segmentEventPush({
          event: 'Pdp Product Image Details Interaction',
          event_category: categoryName,
          event_action: 'full screen',
          event_label: 'press & hold',
          video_name: videoName,
          product: [{
            product_brand: productDetails?.brandName,
            product_brick: productDetails?.brickName,
            product_id: productDetails?.code || productDetails?.baseProduct,
            product_list_price: productDetails?.price?.discountValue,
            product_mrp: productDetails?.price?.value,
            product_name: productDetails?.name,
            product_rating: productDetails?.aggregateRating?.averageRating,
          }],
          video_length: position,
          video_seen: videoEnded ? true : false,
          video_pause_resume: isPlaying ? 'pause' : 'resume',
          video_mute: mute ?  'false' : 'true',
          video_impression: true,
          video_screen: isModal ? 'fullscreen' : 'minimized',
      });
      }
    }
  };

  const handleVideoProgress = ({ target: { value } }) => {
    const manualChange = Number(value);
    videoRef.current.currentTime = (duration / 100) * manualChange;
    setPosition((duration / 100) * manualChange);
  };

  const imageBaseRef = `${properties.getProperty('imageBaseUrl')}/static/img/`;

  const longPressRelatedProps = {};
  if (isModal && deviceType === 'msite' && !isShopThelook) {
    longPressRelatedProps.onTouchStart = onTouchStartHandler;
    longPressRelatedProps.onTouchEnd = onTouchEndHandler;
  }

  const showPlayIconForMsite = useMemo(() => {
    const result =
      showPlayPauseHandler &&
      !videoEnded &&
      !loading &&
      !isPlaying &&
      !loop &&
      deviceType === 'msite' &&
      (!isModal || isShopThelook || isPDP || showPlayIcon);
    return result;
  }, [
    showPlayPauseHandler,
    videoEnded,
    loading,
    isPlaying,
    deviceType,
    isModal,
    isShopThelook,
    isPDP,
    loop
  ]);

  const showRePlayButton = useMemo(() => {
    const result =
      videoEnded && !loading && (isModal || (!isModal && isPDP) || (!isModal && !loop));
    return result;
  }, [videoEnded, loading, isModal, isPDP, loop]);


  return (
    <div
      className={cx(
        'videoContainer',
        !isKnowTheProduct && 'videoContainerpdp',
        isFleekPdp && 'pdpVideoContainer',
        { loadingBackground: loading && !isFleek },
        { loadingBackgroundFleek: loading && isFleek }
      )}
      ref={inViewRef}
    >
      {isWaiting && !loading && !isLoadingError && (
        <button
          aria-label="video"
          className={cx('modalReplay', { floatingWidgetBuffer: isFloatingWidget && !isModal })}
          aria-label="video buffer"
        >
          <img
            className={cx('imageIcon')}
            src={`${imageBaseRef}video_buffer.svg`}
            alt="Buffer icon"
          />
        </button>
      )}

      {isModal && !loading && displayTopGradient && <div className={cx('top-gradient-box')} />}
      {!isModal && !loading && displayBottomGradient && (
        <div className={cx('bottom-gradient-box')} />
      )}
      {isModal && videoEnded && isShopThelook && !loading && (
        <div className={cx('overlayGradient')} />
      )}
      {loading && !isLoadingError && (
        <VideoShimmer
          isFloatingWidget={isFloatingWidget}
          displayBottomGradient={displayBottomGradient}
          isModal={isModal}
        />
      )}
      {showRePlayButton ? (
        <button aria-label="replay video" className={cx('modalReplay')} onClick={replayHandler}>
          <img alt="Replay icon" className="imageIcon" src={`${imageBaseRef}replay.svg`} />
        </button>
      ) : null}

      {displayProgressBar && !videoEnded && !loading && (
        <div className="progressBarContainer">
          <ProgressBar percent={Math.round((position / duration) * 100)} />
        </div>
      )}
      {showPlayIconForMsite ? (
        <button aria-label="play video" className={cx('playButton')} onClick={bannerPlayHandler}>
          <img alt="Play icon" className="imageIcon" src={`${imageBaseRef}play_button.svg`} />
        </button>
      ) : null}
      {/* {showPlayPauseHandler &&
        !videoEnded &&
        !loading &&
        !isPlaying &&
        deviceType === 'msite' &&
        (!isModal || isShopThelook || isPDP) && (
          <button aria-label="play video" className={cx('playButton')} onClick={bannerPlayHandler}>
            <img alt="Play icon" className="imageIcon" src={`${imageBaseRef}play_button.svg`} />
          </button>
        )} */}
      {showPauseButton && deviceType === 'msite' && pdpVideos && (
        <button aria-label="video pause" className={cx('playButton')}  onClick={playPauseHandler}>
          <img
            alt="Pause icon"
            className="imageIcon"
            src={`${imageBaseRef}pause_button_mweb.svg`}
          />
        </button>
      )}  
      {showPauseButton && deviceType === 'msite' && isPDP && (
        <button aria-label="video pause" className={cx('playButton')}>
          <img
            alt="Pause icon"
            className="imageIcon"
            src={`${imageBaseRef}pause_button_mweb.svg`}
          />
        </button>
      )}
      {showPlayPauseHandler && !loading && deviceType === 'desktop' && !videoEnded && (
        <button
          aria-label="video pause"
          className={cx('playPauseToggleDesktop')}
          onClick={playPauseHandler}
        >
          {isPlaying ? (
            <img alt="Pause icon" className="imageIcon" src={`${imageBaseRef}pause_button.svg`} />
          ) : (
            <img
              alt="Play icon"
              className="imageIcon"
              src={`${imageBaseRef}play_button_desktop.svg`}
            />
          )}
        </button>
      )}

      {!loading && !isModal && isPlaying && isPDP && (
        <div className="iconWrapper">
        <button
          aria-label={isFullScreen ? 'Exit full screen video' : 'Enter full screen video'}
          className={cx('pdpBannerFullScreen','targetSize')}
          onClick={expandVideoOnClick}
        >
          <img
            className="imageIcon"
            src={`${imageBaseRef}full_screen.svg`}
            alt=""
          />
        </button>
        </div>
      )}

      {!loading && !isModal && onClickOpenModal && (
        <div className="iconWrapper">
        <button
          aria-label={isFullScreen ? 'Exit full screen video' : 'Enter full screen video'}
          className={cx(pdpVideos ? 'fullScreenPDPVideos' : 'bannerFullScreen','targetSize')}
          onClick={!pdpVideos ? pauseVideoOnClick : handleMinimizeClick}
        >
          <img
            className="imageIcon"
            src={ !pdpVideos ? `${imageBaseRef}full_screen.svg` : `${imageBaseRef}pdpFullScreen.svg`}
            alt=""
          />
        </button>
        </div>
      )}
      {!loading && !isModal && isPlaying && (
        <button
          aria-label={mute ? 'audio off' : 'audio-on'}
          style={{ marginRight: muteIconRightMargin || '' }}
          className={cx(
            `${muteIconRight ? 'align-icon-right' : ''}`,
            `${
              pdpVideos ? 
              'pdpVideoMuteToggle' :
              isPDP
                ? 'pdpBannerMuteToggle'
                : onClickOpenModal
                ? 'bannerMuteToggle'
                : isFleekPdp
                ? 'fleekTopRightMute'
                : 'bannerFullScreen'
            }`,
            isKnowTheProduct && 'fleekPdpBannerFullScreen','targetSize'
          )}
          onClick={muteHandler}
        >
          {mute ? (
            <img
              alt="Audio off icon"
              className="imageIcon"
              src={
                isFleek
                  ? pdpVideos ? `${imageBaseRef}pdpMute.svg`  : getImagePath('audio_off_fleek.svg')
                  : !pdpVideos ? `${imageBaseRef}audio_off_banner.svg` 
                  : `${imageBaseRef}pdpMute.svg`
              }
            />
          ) : (
            <img
              alt="Audio on icon"
              className="imageIcon"
              src={pdpVideos ? `${imageBaseRef}pdpVideoUnMute.svg` 
              : `${imageBaseRef}audio_on_banner.svg`}
            />
          )}
        </button>
      )}
      {!loading && isModal && isPlaying && (
        <button
          className={cx(
            'modalMuteToggle',
            { desktopMuteToggle: deviceType === 'desktop' },
            { msiteShopTheLookMute: scrollBarExits },'targetSize'
          )}
          onClick={muteHandler}
          aria-label={mute ? 'mute' : 'unmute'}
        >
          {mute ? (
            <img
              alt="Audio off icon"
              className="imageIcon"
              src={`${imageBaseRef}audio_off_modal.svg`}
            />
          ) : (
            <img
              alt="Audio on icon"
              className="imageIcon"
              src={pdpVideos ? `${imageBaseRef}pdpVideoUnMute.png` 
              : `${imageBaseRef}audio_on_modal.svg`}
            />
          )}
        </button>
      )}
      {!loading && isPDP && deviceType === 'msite' && (
        <div className={cx('bottomProgressBarContainer', isModal ? 'pdp-modal' : 'pdp')}>
          {isPlaying && (
            <div className={cx('videoProgress')}>
              <input
                className={cx('progress')}
                type="range"
                min="0"
                max="100"
                value={!videoEnded ? Math.round((position / duration) * 100) : 0}
                onChange={!videoEnded ? e => handleVideoProgress(e) : undefined}
                style={{
                  background: `linear-gradient(to right, #fff 0%, #fff ${Math.round(
                    (position / duration) * 100
                  )}%, #6d6d6d ${Math.round((position / duration) * 100)}%, #6d6d6d 100%)`
                }}
              />
            </div>
          )}
          <div className={cx('time')}>
            <Text variant="T2" color="White">
              {formatDuration(remainingTime)}
            </Text>
          </div>
        </div>
      )}
      {!loading && deviceType === 'msite' && !videoEnded && showTimestamp && (
        <div
          className={cx(
            `${isModal ? 'topRightTime' : pdpVideos ? 'bottomLeftTime' :'bottomRightTime'}`,
            isKnowTheProduct && 'pdpRightTime',
            isFleekPdp && 'fleekRightTime',
            {
              msiteShopTheLookTime: isModal && isShopThelook
            }
          )}
        >
          {formatDuration(remainingTime)}
        </div>
      )}
      <div
        className={cx('videoPlayer')}
        style={{
          height: loading ? '0' : isKnowTheProduct ? 'auto' : '100%'
        }}
      >
        <video
          ref={videoRef}
          style={{
            display: loading ? 'none' : 'block',
            borderRadius: `${borderRadius}px`,
            objectFit: objectFit
              ? objectFit
              : deviceType !== 'desktop' && isShopThelook
              ? 'fill'
              : 'contain'
          }}
          {...(autoPlay && videoIsOnView && { autoPlay })}
          {...(muted && { muted })}
          {...(thumbnailImage.length > 0 && { poster: thumbnailImage })}
          playsInline
          onClick={pauseVideoOnClick}
          onTimeUpdate={positionChangeHandler}
          onDurationChange={durationChangeHandler}
          onEnded={videoEndedHandler}
          onError={() => setIsLoadingError(true)}
          onLoadedData={onReady}
          width="100%"
          height={`${isKnowTheProduct ? 'auto' : '100%'}`}
          onLoadStartCapture={loadStartHandler}
          onCanPlayThrough={canPlayThroughHandler}
          {...longPressRelatedProps}
        >
          <source src={replaceWithJioCDN(videoURL)} type="video/mp4" />
        </video>
      </div>
    </div>
  );
}
export default Video;
